/**
 * Push notification registration for Capacitor (iOS + Android).
 *
 * On mobile: request permission, register with APNs/FCM, POST the returned
 * token to /api/me/device-tokens so the backend can push us later.
 * On web / desktop: no-op (Capacitor plugin is unavailable, PushNotifications
 * import would throw). We check `Capacitor.isNativePlatform()` first.
 *
 * The registration runs at most once per app session — a repeated launch
 * with the same token becomes a no-op via the backend's upsert.
 *
 * Also owns deep-link routing (`registerDeepLinks`): both cold-boot and
 * warm-start `drivon://` custom scheme + `https://drivons.com/*` Universal
 * Links are funnelled through a single `routeDeepLink()` helper so push
 * taps and email/SMS taps hit the same router.
 */
import { toast } from "sonner";
import api from "./api";
import { getAppMode } from "./app-mode";

let _registered = false;
let _deepLinksRegistered = false;

async function loadCapacitor() {
  // Dynamic imports so the web bundle doesn't crash when the packages resolve
  // but their native counterparts don't exist. The bundle DOES include them
  // (they're listed in package.json), so import()' resolution succeeds — we
  // just skip actual registration on non-native platforms.
  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return { Capacitor, PushNotifications };
}

/**
 * Map an incoming deep-link URL to an in-app hash route.
 *
 * Accepts BOTH the custom scheme (`drivon://driver/orders/:id`) and the
 * https Universal-Link form (`https://drivons.com/orders/:id`). Anything
 * we don't recognize is dropped silently — we never want a malformed
 * link to leak into `window.location.hash` and break the router.
 *
 * Exported so tests (and cold-boot handling below) can share the exact
 * same routing table as warm-start push taps.
 */
export function routeDeepLink(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;

  // Normalize both scheme styles to a single "/path" we can pattern-match.
  //   drivon://driver/orders/abc      → /driver/orders/abc
  //   https://drivons.com/driver/wallet → /driver/wallet
  let path = "";
  try {
    const u = new URL(rawUrl);
    if (u.protocol === "drivon:") {
      // URL parses `drivon://driver/orders/abc` as host="driver",
      // pathname="/orders/abc" — reassemble.
      path = `/${u.host}${u.pathname || ""}`.replace(/\/+$/, "");
    } else if (u.protocol === "https:" && u.hostname === "drivons.com") {
      path = u.pathname.replace(/\/+$/, "");
    } else {
      return null; // foreign scheme — ignore
    }
  } catch {
    return null;
  }

  // Rider order tracking (email receipt link).
  //   /order/:id  or  /orders/:id
  const orderMatch = path.match(/^\/orders?\/([A-Za-z0-9_-]+)$/);
  if (orderMatch) {
    return `#/orders/${encodeURIComponent(orderMatch[1])}`;
  }

  // Driver order actions (push tap + email/SMS receipt).
  //   /driver/orders/:id
  const driverOrderMatch = path.match(/^\/driver\/orders\/([A-Za-z0-9_-]+)$/);
  if (driverOrderMatch) {
    return `#/supplier?order=${encodeURIComponent(driverOrderMatch[1])}`;
  }

  // Driver wallet / earnings shortcuts (referral & weekly-earnings pushes).
  if (path === "/driver/wallet") return "#/supplier?tab=wallet";
  if (path === "/driver/earnings") return "#/supplier?tab=earnings";

  // Passenger split-payment invitation link.
  const splitMatch = path.match(/^\/split\/([A-Za-z0-9_-]+)$/);
  if (splitMatch) {
    return `#/split/${encodeURIComponent(splitMatch[1])}`;
  }

  // Referral link (Universal Link only, no custom-scheme equivalent).
  const refMatch = path.match(/^\/r\/([A-Za-z0-9_-]+)$/);
  if (refMatch) {
    return `#/?ref=${encodeURIComponent(refMatch[1])}`;
  }

  return null;
}

function _applyDeepLink(rawUrl) {
  const target = routeDeepLink(rawUrl);
  if (target) {
    window.location.hash = target;
  }
}

/**
 * Wire the Capacitor App plugin's URL-open listener AND consume the cold-
 * boot launch URL (getLaunchUrl). Warm-start emits `appUrlOpen`; cold-boot
 * emits both — we drain `getLaunchUrl` once at boot to handle the case
 * where the listener wasn't attached in time on the very first launch.
 *
 * Safe to call on web/desktop — the Capacitor App plugin returns
 * `isNativePlatform === false` and we bail out.
 */
export async function registerDeepLinks() {
  if (_deepLinksRegistered) return;
  _deepLinksRegistered = true;

  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return;

  const { App } = await import("@capacitor/app");

  // Warm-start / background-return.
  App.addListener("appUrlOpen", (event) => {
    _applyDeepLink(event?.url);
  });

  // Cold-boot: launched from a link. Runs once, best-effort.
  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) _applyDeepLink(launch.url);
  } catch {
    // Older Capacitor / non-supporting platform — nothing we can do.
  }
}

/**
 * Register the current device to receive push notifications for the signed-in
 * user. Safe to call unconditionally on app boot — it self-guards against
 * being invoked more than once and against non-native environments.
 */
export async function registerPush() {
  // Deep-link routing is independent of push permission — attach it on
  // every native boot even if the user later denies notification perms.
  registerDeepLinks();

  if (_registered) return;
  _registered = true; // guard even if the below throws — no thundering herd

  const loaded = await loadCapacitor();
  if (!loaded) return; // Web — nothing to do
  const { Capacitor, PushNotifications } = loaded;
  const platform = Capacitor.getPlatform(); // "ios" | "android"

  // Listeners MUST be attached before .register() otherwise we miss the
  // very first registration event (documented Capacitor quirk).
  await PushNotifications.addListener("registration", async ({ value }) => {
    try {
      await api.post("/me/device-tokens", {
        token: value,
        platform,
        app: getAppMode(), // "passenger" | "driver"
      });
    } catch (err) {
      // Non-fatal — we still show the app; user just won't get pushes until next launch.
      console.warn("[push] backend registration failed:", err);
    }
  });

  await PushNotifications.addListener("registrationError", (err) => {
    console.error("[push] registration error:", err);
  });

  await PushNotifications.addListener("pushNotificationReceived", (notif) => {
    // App is foregrounded — no OS banner. Render our own toast so the driver
    // still sees the ride offer while looking at the app.
    toast.message(notif.title || "Drivons", {
      description: notif.body || "",
      duration: 8000,
    });
  });

  await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    // User tapped a notification while app was backgrounded/killed. Route
    // through the shared deep-link helper so push taps and email/SMS taps
    // stay in lockstep.
    const data = event.notification?.data;
    _applyDeepLink(data?.deep_link || "");
  });

  // Permission dance: check → prompt → register.
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") {
    console.info("[push] permission denied");
    return;
  }

  // Android 8+ needs an explicit notification channel matching the FCM payload.
  if (platform === "android") {
    try {
      await PushNotifications.createChannel({
        id: "ride-offers",
        name: "Ride offers",
        description: "New ride offers dispatched to you",
        importance: 5, // MAX — bypass Do Not Disturb
        sound: "default",
        vibration: true,
      });
    } catch (err) {
      console.warn("[push] createChannel failed:", err);
    }
  }

  await PushNotifications.register();
}

/**
 * On logout: tell the backend to stop pushing to this device so someone
 * else who logs in later doesn't inherit our notifications.
 */
export async function unregisterPush(token) {
  if (!token) return;
  try {
    await api.delete(`/me/device-tokens/${encodeURIComponent(token)}`);
  } catch (err) {
    console.warn("[push] backend unregister failed:", err);
  }
  _registered = false;
}
